class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);


if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('cart-discount-code')) {
  customElements.define(
    'cart-discount-code',
    class CartDiscountCode extends HTMLElement {
      constructor() {
        super();
        
        this.button = this.querySelector('.field__button');
        this.input = this.querySelector('.field__input');
        
        // Find the specific error and success elements for this discount code instance
        // Check if this is the main cart or cart drawer discount code
        const isMainCart = this.input.id === 'Cart-DiscountCode';
        const errorId = isMainCart ? 'Cart-DiscountError' : 'CartDrawer-DiscountError';
        const successId = isMainCart ? 'Cart-DiscountSuccess' : 'CartDrawer-DiscountSuccess';
        
        this.errorElement = document.getElementById(errorId);
        this.successElement = document.getElementById(successId);
        
        this.button.addEventListener('click', this.applyDiscount.bind(this));
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.applyDiscount();
          }
        });
        
        // Clear localStorage when user manually clears the input
        this.input.addEventListener('input', (event) => {
          if (event.target.value.trim() === '') {
            localStorage.removeItem('shopify_discount_code');
            this.hideMessages();
          }
        });
        
        // Display existing discount code if one is stored
        this.displayStoredDiscountCode();
      }

      async applyDiscount() {
        const discountCode = this.input.value.trim();
        
        if (!discountCode) {
          this.showError(window.cartStrings?.discountError || 'Please enter a discount code');
          return;
        }

        this.setLoading(true);
        this.hideMessages();

        try {
          // Enhanced validation: Try to apply the discount and check the response
          await this.validateAndApplyDiscount(discountCode);
        } catch (error) {
          console.error('Error applying discount:', error);
          localStorage.removeItem('shopify_discount_code');
          this.showError(window.cartStrings?.discountError || 'Unable to apply discount code');
        }

        this.setLoading(false);
      }

      async validateAndApplyDiscount(discountCode) {
        try {
          // First, validate the discount code exists
          const validationResponse = await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
            method: 'HEAD',
            headers: {
              'Accept': 'application/json',
            }
          });

          if (!validationResponse.ok && validationResponse.status !== 302) {
            // Discount code doesn't exist - remove from localStorage and show error
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'Invalid discount code');
            return;
          }

          // Now try to apply the discount using cart form submission
          const formData = new FormData();
          formData.append('discount', discountCode);
          
          const response = await fetch('/cart', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            // Wait a moment for the discount to be processed
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Get updated cart data to check if discount was applied
            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();
            
            // Check for both cart-level and line-level discounts
            const hasCartLevelDiscount = cartData.cart_level_discount_applications && 
                                       cartData.cart_level_discount_applications.length > 0;
            
            const hasLineItemDiscounts = cartData.items && cartData.items.some(item => 
              item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
            );
            
            // Check if our specific discount code was applied
            const appliedDiscount = this.findAppliedDiscount(cartData, discountCode);
            
            if (appliedDiscount) {
              // Discount was successfully applied - store in localStorage
              localStorage.setItem('shopify_discount_code', discountCode);
              
              let totalSavings = 0;
              let discountMessage = `Discount "${discountCode}" applied!`;
              
              // Calculate total savings
              if (hasCartLevelDiscount) {
                const cartLevelDiscount = cartData.cart_level_discount_applications.find(
                  discount => discount.title.toLowerCase() === discountCode.toLowerCase()
                );
                if (cartLevelDiscount) {
                  totalSavings += cartLevelDiscount.total_allocated_amount || 0;
                }
              }
              
              if (hasLineItemDiscounts) {
                cartData.items.forEach(item => {
                  if (item.line_level_discount_allocations) {
                    item.line_level_discount_allocations.forEach(discount => {
                      if (discount.discount_application.title.toLowerCase() === discountCode.toLowerCase()) {
                        totalSavings += discount.amount || 0;
                      }
                    });
                  }
                });
              }
              
              const savingsFormatted = (totalSavings / 100).toFixed(2);
              if (totalSavings > 0) {
                discountMessage += ` Save €${savingsFormatted}`;
              }
              
              this.showSuccess(window.cartStrings?.discountApplied || discountMessage);
              
              // Force refresh the main cart footer if we're on the main cart page
              const isMainCartPage = window.location.pathname === '/cart';
              if (isMainCartPage) {
                this.updateCartDisplay();
              }
              
              this.refreshCart();
              return;
            } else {
              // Discount was not applied - need to check if it's a valid code that just doesn't apply to current cart
              // or if it's an invalid code
              await this.checkDiscountValidity(discountCode);
            }
          } else {
            // HTTP error - remove from localStorage and show generic error
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'Unable to apply discount code');
          }
          
        } catch (error) {
          console.error('Error in validateAndApplyDiscount:', error);
          localStorage.removeItem('shopify_discount_code');
          this.showError(window.cartStrings?.discountError || 'Unable to apply discount code');
        }
      }

      findAppliedDiscount(cartData, discountCode) {
        // Check cart-level discounts
        if (cartData.cart_level_discount_applications) {
          const cartDiscount = cartData.cart_level_discount_applications.find(
            discount => discount.title.toLowerCase() === discountCode.toLowerCase()
          );
          if (cartDiscount) return cartDiscount;
        }
        
        // Check line-level discounts
        if (cartData.items) {
          for (const item of cartData.items) {
            if (item.line_level_discount_allocations) {
              const lineDiscount = item.line_level_discount_allocations.find(
                discount => discount.discount_application.title.toLowerCase() === discountCode.toLowerCase()
              );
              if (lineDiscount) return lineDiscount;
            }
          }
        }
        
        return null;
      }

      async checkDiscountValidity(discountCode) {
        try {
          // Try to get more information about why the discount didn't apply
          // by checking the response from the cart submission
          const formData = new FormData();
          formData.append('discount', discountCode);
          
          const response = await fetch('/cart', {
            method: 'POST',
            body: formData
          });
          
          const responseText = await response.text();
          
          // Check for specific error patterns in the response
          if (responseText.includes('invalid') || responseText.includes('not found') || responseText.includes('does not exist')) {
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'Invalid discount code');
          } else if (responseText.includes('expired') || responseText.includes('ended')) {
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'This discount code has expired');
          } else if (responseText.includes('minimum') || responseText.includes('spend')) {
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'Minimum order requirement not met');
          } else if (responseText.includes('already applied')) {
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'Discount code already applied');
          } else if (responseText.includes('not applicable') || responseText.includes('products')) {
            localStorage.removeItem('shopify_discount_code');
            this.showError(window.cartStrings?.discountError || 'Discount code not applicable to current products');
          } else {
            // If no specific error found, the code might be valid but only apply at checkout
            // Store it for checkout and show success message
            localStorage.setItem('shopify_discount_code', discountCode);
            this.showSuccess(window.cartStrings?.discountApplied || `Discount code "${discountCode}" will be applied at checkout`);
            
            // Force refresh the main cart footer if we're on the main cart page
            const isMainCartPage = window.location.pathname === '/cart';
            if (isMainCartPage) {
              this.updateCartDisplay();
            }
            
            this.refreshCart();
          }
          
        } catch (error) {
          console.error('Error checking discount validity:', error);
          // If we can't determine the specific error, remove from localStorage and show generic error
          localStorage.removeItem('shopify_discount_code');
          this.showError(window.cartStrings?.discountError || 'Invalid discount code');
        }
      }

      async handleDiscountApplicationError(discountCode) {
        // Simplified error handling - just show generic error
        this.showError(window.cartStrings?.discountError || 'Invalid discount code');
      }

      async applyDiscountToCart(discountCode) {
        // This method is now deprecated - using validateAndApplyDiscount instead
        await this.validateAndApplyDiscount(discountCode);
      }

      setLoading(loading) {
        this.button.disabled = loading;
        this.button.textContent = loading 
          ? (window.cartStrings?.applying || 'Applying...') 
          : (window.cartStrings?.applyDiscount || 'Apply');
      }

      showError(message) {
        this.errorElement.querySelector('.cart-discount__error-text').textContent = message;
        this.errorElement.hidden = false;
        this.successElement.hidden = true;
      }

      showSuccess(message) {
        this.successElement.querySelector('.cart-discount__success-text').textContent = message;
        this.successElement.hidden = false;
        this.errorElement.hidden = true;
      }

      hideMessages() {
        this.errorElement.hidden = true;
        this.successElement.hidden = true;
      }

      async displayStoredDiscountCode() {
        const storedCode = localStorage.getItem('shopify_discount_code');
        if (storedCode) {
          this.input.value = storedCode; // Set the input value to show the stored code
          
          // Check if there are active discount applications in the cart
          try {
            const response = await fetch('/cart.js');
            const cartData = await response.json();
            
            // Check if our stored discount is actually applied to the cart
            const appliedDiscount = this.findAppliedDiscount(cartData, storedCode);
            
            if (appliedDiscount) {
              // Discount is actively applied to the cart
              this.showSuccess(window.cartStrings?.discountApplied || `Discount "${storedCode}" is active`);
            } else {
              // Discount code is stored but not applied - validate if it's still valid
              try {
                const validationResponse = await fetch(`/discount/${encodeURIComponent(storedCode)}`, {
                  method: 'HEAD',
                  headers: {
                    'Accept': 'application/json',
                  }
                });
                
                if (validationResponse.ok || validationResponse.status === 302) {
                  // Discount code is still valid but not applied to current cart
                  this.showSuccess(window.cartStrings?.discountApplied || `Discount "${storedCode}" will be applied at checkout`);
                } else {
                  // Discount code is no longer valid - remove from localStorage and clear input
                  localStorage.removeItem('shopify_discount_code');
                  this.input.value = '';
                  this.hideMessages();
                }
              } catch (validationError) {
                console.error('Error validating stored discount code:', validationError);
                // If validation fails, remove the stored code to be safe
                localStorage.removeItem('shopify_discount_code');
                this.input.value = '';
                this.hideMessages();
              }
            }
          } catch (error) {
            console.error('Error checking cart discount status:', error);
            // If we can't check the cart status, remove the stored code to be safe
            localStorage.removeItem('shopify_discount_code');
            this.input.value = '';
            this.hideMessages();
          }
        }
      }

      refreshCart() {
        // Trigger cart refresh to update totals and discount display
        const cartRefreshEvent = new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'discount-code' }
        });
        document.dispatchEvent(cartRefreshEvent);
        
        // Refresh cart drawer by fetching updated cart data and re-rendering
        this.refreshCartDrawer();
      }

      async refreshCartDrawer() {
        try {
          // Check if we're on the main cart page or in cart drawer
          const isMainCartPage = window.location.pathname === '/cart';
          
          // Get sections for both main cart and cart drawer
          const cartDrawerSections = [
            {
              id: 'CartDrawer',
              section: 'cart-drawer',
              selector: '.drawer__inner',
            },
            {
              id: 'cart-icon-bubble',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            }
          ];

          // Add main cart footer to ensure totals are updated
          const mainCartFooterSection = {
            id: 'main-cart-footer',
            section: 'main-cart-footer',
            selector: '.js-contents',
          };

          // Get all sections that need to be updated
          const allSections = cartDrawerSections.map((section) => section.section)
            .concat([mainCartFooterSection.section])
            .concat(this.getSectionsToRender().map((section) => section.section));

          // Use a GET request to fetch updated sections with current cart state
          const sectionsParam = allSections.join(',');
          const response = await fetch(`${window.location.pathname}?sections=${sectionsParam}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            const parsedState = await response.json();
            
            // Update cart drawer sections first (only if cart drawer exists)
            if (!isMainCartPage) {
              cartDrawerSections.forEach((section) => {
                const elementToReplace =
                  document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                
                if (elementToReplace && parsedState[section.section]) {
                  elementToReplace.innerHTML = this.getSectionInnerHTML(
                    parsedState[section.section],
                    section.selector
                  );
                }
              });
            }

            // Always update main cart footer section
            const mainCartFooterElement = document.getElementById(mainCartFooterSection.id);
            if (mainCartFooterElement && parsedState[mainCartFooterSection.section]) {
              const elementToReplace = mainCartFooterElement.querySelector(mainCartFooterSection.selector) || mainCartFooterElement;
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState[mainCartFooterSection.section],
                mainCartFooterSection.selector
              );
            }

            // Update main cart sections
            this.getSectionsToRender().forEach((section) => {
              const elementToReplace =
                document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
              
              if (elementToReplace && parsedState[section.section]) {
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                  parsedState[section.section],
                  section.selector
                );
              }
            });

            // Get updated cart data to update classes
            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();

            // Update cart drawer wrapper classes (only if cart drawer exists)
            if (!isMainCartPage) {
              const cartDrawerWrapper = document.querySelector('cart-drawer');
              if (cartDrawerWrapper) {
                cartDrawerWrapper.classList.toggle('is-empty', cartData.item_count === 0);
              }
            }

            // Update main cart footer classes
            const cartFooter = document.getElementById('main-cart-footer');
            if (cartFooter) {
              cartFooter.classList.toggle('is-empty', cartData.item_count === 0);
            }

            // Update main cart wrapper classes
            const mainCart = document.getElementById('main-cart-items');
            if (mainCart) {
              mainCart.classList.toggle('is-empty', cartData.item_count === 0);
            }
          }
          
        } catch (error) {
          console.error('Error refreshing cart drawer:', error);
          // Fallback to a more aggressive refresh approach
          try {
            // Force a complete cart refresh by reloading the cart drawer content
            const cartDrawerResponse = await fetch('/?section_id=cart-drawer');
            const cartDrawerHTML = await cartDrawerResponse.text();
            
            const parser = new DOMParser();
            const doc = parser.parseFromString(cartDrawerHTML, 'text/html');
            const newCartDrawerContent = doc.querySelector('#CartDrawer .drawer__inner');
            
            if (newCartDrawerContent) {
              const currentCartDrawerInner = document.querySelector('#CartDrawer .drawer__inner');
              if (currentCartDrawerInner) {
                currentCartDrawerInner.innerHTML = newCartDrawerContent.innerHTML;
              }
            }
            
            // Also refresh main cart if present
            if (document.getElementById('main-cart-items')) {
              const mainCartResponse = await fetch('/?section_id=main-cart-items');
              const mainCartHTML = await mainCartResponse.text();
              
              const mainCartDoc = parser.parseFromString(mainCartHTML, 'text/html');
              const newMainCartContent = mainCartDoc.querySelector('#main-cart-items .js-contents');
              
              if (newMainCartContent) {
                const currentMainCartContent = document.querySelector('#main-cart-items .js-contents');
                if (currentMainCartContent) {
                  currentMainCartContent.innerHTML = newMainCartContent.innerHTML;
                }
              }
            }
            
          } catch (fallbackError) {
            console.error('Fallback refresh also failed:', fallbackError);
            // Last resort: simple cart data update
            this.updateCartDisplay();
          }
        }
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      async updateCartDisplay() {
        try {
          const response = await fetch('/cart.js');
          const cartData = await response.json();
          
          // Update cart totals in both cart drawer and main cart
          const totalElements = document.querySelectorAll('.totals__total-value');
          totalElements.forEach(element => {
            if (element) {
              // Format the total price with proper currency
              const formattedPrice = new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: cartData.currency || 'EUR'
              }).format(cartData.total_price / 100);
              
              element.textContent = formattedPrice;
            }
          });

          // Update discount display in both cart drawer and main cart
          const discountElements = document.querySelectorAll('.discounts');
          discountElements.forEach(discountList => {
            if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
              discountList.style.display = 'block';
              
              // Update discount list content
              const discountItems = cartData.cart_level_discount_applications.map(discount => {
                const savings = discount.total_allocated_amount ? (discount.total_allocated_amount / 100).toFixed(2) : '0.00';
                return `<li class="discounts__discount discounts__discount--end">
                  ${discount.title} (-€${savings})
                </li>`;
              }).join('');
              
              discountList.innerHTML = discountItems;
            } else {
              discountList.style.display = 'none';
            }
          });

          // Update cart item prices if they have discounts
          if (cartData.items) {
            cartData.items.forEach((item, index) => {
              const line = index + 1;
              const cartItem = document.querySelector(`#CartItem-${line}`) || document.querySelector(`#CartDrawer-Item-${line}`);
              
              if (cartItem && item.final_line_price !== item.original_line_price) {
                // Update price display to show discount
                const priceElement = cartItem.querySelector('.cart-item__price');
                if (priceElement) {
                  const originalPrice = (item.original_line_price / 100).toFixed(2);
                  const finalPrice = (item.final_line_price / 100).toFixed(2);
                  priceElement.innerHTML = `
                    <span class="cart-item__price--strikethrough">€${originalPrice}</span>
                    <span class="cart-item__price--final">€${finalPrice}</span>
                  `;
                }
              }
            });
          }

          // Specifically update main cart footer totals
          const mainCartFooter = document.getElementById('main-cart-footer');
          if (mainCartFooter) {
            const mainCartTotalElement = mainCartFooter.querySelector('.totals__total-value');
            if (mainCartTotalElement) {
              const formattedPrice = new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: cartData.currency || 'EUR'
              }).format(cartData.total_price / 100);
              
              mainCartTotalElement.textContent = formattedPrice;
            }

            // Update main cart discount display
            const mainCartDiscounts = mainCartFooter.querySelector('.discounts');
            if (mainCartDiscounts) {
              if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
                mainCartDiscounts.style.display = 'block';
                
                const discountItems = cartData.cart_level_discount_applications.map(discount => {
                  const savings = discount.total_allocated_amount ? (discount.total_allocated_amount / 100).toFixed(2) : '0.00';
                  return `<li class="discounts__discount discounts__discount--position">
                    ${discount.title} (-€${savings})
                  </li>`;
                }).join('');
                
                mainCartDiscounts.innerHTML = discountItems;
              } else {
                mainCartDiscounts.style.display = 'none';
              }
            }
          }
          
        } catch (error) {
          console.error('Error updating cart display:', error);
          // Fallback to page reload if update fails
          window.location.reload();
        }
      }

    }
  );
}

// Global checkout handler function
window.handleCheckout = function() {
  // Get the stored discount code from localStorage
  const discountCode = localStorage.getItem('shopify_discount_code');
  let checkoutUrl = '/checkout';
  
  if (discountCode) {
    checkoutUrl += `?discount=${encodeURIComponent(discountCode)}`;
  }
  
  window.location.href = checkoutUrl;
};

// Enhanced checkout handler that ensures discount is applied
window.handleCheckoutWithDiscount = async function() {
  try {
    // Get the stored discount code
    const discountCode = localStorage.getItem('shopify_discount_code');
    
    if (discountCode) {
      // First, ensure the discount is applied to the cart
      const formData = new FormData();
      formData.append('discount', discountCode);
      
      const applyResponse = await fetch('/cart', {
        method: 'POST',
        body: formData
      });
      
      if (applyResponse.ok) {
        // Wait a moment for the discount to be processed
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    // Now redirect to checkout with the discount code
    let checkoutUrl = '/checkout';
    if (discountCode) {
      checkoutUrl += `?discount=${encodeURIComponent(discountCode)}`;
    }
    
    window.location.href = checkoutUrl;
    
  } catch (error) {
    console.error('Error applying discount before checkout:', error);
    // Fallback to regular checkout
    window.handleCheckout();
  }
};

// Function to ensure discount is applied before form submission
window.ensureDiscountApplied = async function(event) {
  try {
    const discountCode = localStorage.getItem('shopify_discount_code');
    
    if (discountCode) {
      // Prevent default form submission
      event.preventDefault();
      
      // Apply discount to cart first
      const formData = new FormData();
      formData.append('discount', discountCode);
      
      const applyResponse = await fetch('/cart', {
        method: 'POST',
        body: formData
      });
      
      if (applyResponse.ok) {
        // Wait a moment for the discount to be processed
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Now submit the form
        const form = event.target.closest('form');
        if (form) {
          form.submit();
        } else {
          // If no form found, redirect to checkout with discount
          window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
        }
      } else {
        // If discount application fails, submit form anyway
        const form = event.target.closest('form');
        if (form) {
          form.submit();
        } else {
          // If no form found, redirect to checkout
          window.location.href = '/checkout';
        }
      }
    } else {
      // If no discount code, allow normal form submission
      return true;
    }
    
  } catch (error) {
    console.error('Error ensuring discount applied:', error);
    // On error, try to submit form or redirect to checkout
    const form = event.target.closest('form');
    if (form) {
      form.submit();
    } else {
      window.location.href = '/checkout';
    }
  }
  
  // Prevent default form submission since we're handling it manually
  return false;
};

